/**
 * api.js — All data operations go through Lark Base via the proxy.
 * No Django backend needed.
 */
import {
  insertMeeting,
  getAllMeetings,
  getMeetingByField,
  saveMeetingTranscript,
  saveTranslation,
  getTranslationsForMeeting,
  saveSummary,
  getSummaryForMeeting,
  deleteMeeting as larkDeleteMeeting,
} from './larkService';

const AI_REWRITE_URL = "/api/rewrite";
const AI_TRANSCRIBE_URL = "/api/ai";

// ── Transcription ──────────────────────────────────────────────

/**
 * Send audio blob to secure AI proxy for transcription.
 */
export const transcribeAudio = async (audioBlob) => {
  try {
    console.log("🎙️ [AI] Calling secure transcription proxy...");
    const response = await fetch(AI_TRANSCRIBE_URL, {
      method: "POST",
      body: audioBlob,
      headers: { "Content-Type": audioBlob.type || "audio/webm" }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Transcription failed");
    }

    return await response.json();
  } catch (err) {
    console.error("❌ [AI] Transcription failed:", err.message);
    throw err;
  }
};

// ── Meeting CRUD ──────────────────────────────────────────────

export const createMeeting = async ({ title = '', transcript = [], full_text = '', language = '' } = {}) => {
  const meeting_id = crypto.randomUUID();
  const meeting = {
    meeting_id,
    title,
    full_text,
    language,
    segment_count: transcript.length,
    status: 'completed',
    transcript,
    translated_text: {},
    summary: {},
    created_at: new Date().toISOString(),
  };
  await insertMeeting(meeting);
  return meeting;
};

export const listMeetings = async () => {
  const meetings = await getAllMeetings();
  return meetings.map((m) => ({
    meeting_id:    m.meeting_id,
    title:         m.title,
    status:        m.status,
    full_text:     m.full_text,
    language:      m.language,
    segment_count: m.segment_count,
    created_at:    m.created_at ? new Date(Number(m.created_at)).toISOString() : null,
    transcript:    m.transcript || [],
    translated_text: {},
    summary: {},
  }));
};

export const getMeeting = async (meetingId) => {
  const m = await getMeetingByField(meetingId);
  if (!m) return null;

  const [translated_text, summary] = await Promise.all([
    getTranslationsForMeeting(meetingId),
    getSummaryForMeeting(meetingId),
  ]);

  // The transcript is stored as a JSON string in the "Transcript JSON" field —
  // parse it here so the loaded meeting keeps its segments instead of going blank.
  let transcript = [];
  try { transcript = JSON.parse(m['Transcript JSON'] || '[]'); } catch { transcript = []; }

  return {
    meeting_id:    m['Meeting ID'],
    title:         m['Title'],
    status:        m['Status'],
    full_text:     m['Full Text'],
    language:      m['Language'],
    transcript,
    translated_text,
    summary,
    created_at:    m['Created At'] ? new Date(Number(m['Created At'])).toISOString() : null,
  };
};

export const saveTranscript = async (meetingId, fullText, language, segments) => {
  await saveMeetingTranscript(meetingId, fullText, language, segments);
};

// ── Translation ───────────────────────────────────────────────

const GOOGLE_TRANSLATE_URL = 'https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&dt=t';

export const translateText = async (text, targetLang) => {
  const url = `${GOOGLE_TRANSLATE_URL}&tl=${targetLang}&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  const json = await res.json();
  return json[0].map((s) => s[0]).join('');
};

export const translateAndSave = async (meetingId, fullText, targetLang) => {
  const translated = await translateText(fullText, targetLang);
  await saveTranslation(meetingId, targetLang, translated);
  return translated;
};

// ── AI Rewriting & Summarization ──────────────────────────────

export const rewriteAndSave = async (meetingId, fullText, mode) => {
  try {
    let systemPrompt = "";
    switch(mode) {
      case 'magic': systemPrompt = "Rewrite this transcript to be professional, polished, and perfectly clear. Fix all grammar, remove filler words, and improve the narrative flow."; break;
      case 'slightly': systemPrompt = "Rewrite this transcript slightly. Keep it very close to the original words but fix obvious errors and improve readability just a bit."; break;
      case 'significantly': systemPrompt = "Significantly rewrite this transcript. Transform the rough notes into a high-quality, professional document with a sophisticated tone."; break;
      case 'structured': systemPrompt = "Transform this transcript into a structured report. Use clear headings (e.g., Overview, Discussion Points, Decisions) and organized sections."; break;
      case 'list': systemPrompt = "Summarize this transcript as a clean, bulleted list of the most important points. Do not include any conversational filler."; break;
      case 'detailed': systemPrompt = "Provide a very detailed, comprehensive summary of this meeting. Cover all topics discussed in depth."; break;
      case 'short': systemPrompt = "Provide a very brief, 2-3 sentence executive summary of this meeting."; break;
      case 'takeaways': systemPrompt = "Extract ONLY the key takeaways, final decisions made, and specific action items with owners if mentioned."; break;
      default: systemPrompt = "Rewrite and summarize this meeting transcript.";
    }

    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const proxyUrl = isLocal ? "http://localhost:4000/api/rewrite" : AI_REWRITE_URL;

    const response = await fetch(proxyUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: systemPrompt, text: fullText, mode }),
    });

    // Guard: if the server returned HTML (e.g. a 404 page) instead of JSON
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error("Rewrite proxy is not running. Please restart npm start.");
    }

    const data = await response.json();
    if (response.status === 503) {
      throw new Error(data.error || "AI is warming up. Please click again in a few seconds.");
    }
    if (!response.ok) throw new Error(data.error || "AI Rewrite failed");

    const aiSummaryText = data.result;
    const lines = aiSummaryText.split(/\n+/).map(l => l.trim().replace(/^[-*•\d.]+\s*/, '')).filter(l => l.length > 5);
    
    const summary = {
      overall_summary: aiSummaryText,
      key_points: lines,
      decisions: [],
      action_items: [],
      mode: mode
    };

    await saveSummary(meetingId, summary);
    return summary;
  } catch (err) {
    console.warn("⚠️ [AI] Secure rewrite failed:", err.message);
    throw err;
  }
};

export const deleteMeeting = async (meetingId) => {
  await larkDeleteMeeting(meetingId);
};
